//* Importign React
import React, { useRef } from "react";

//* Importing modules
import { useDrag, useDrop, DropTargetMonitor } from "react-dnd";

//? Importing types
import { RowProps } from "./types/types";

//* Importing icons
import { BsFillEyeFill } from "react-icons/bs";
import { MdDelete } from "react-icons/md";

const DND_ITEM_TYPE = "row";

/**
 * Represents a draggable table row in the ExamView component.
 *
 * @param {RowProps} props - The properties passed to the Row component.
 * @param {Row} props.row - The data for the current row.
 * @param {number} props.index - The index of the current row in the list.
 * @param {(dragIndex: number, hoverIndex: number) => void} props.moveRow - Function to handle moving a row from one position to another.
 * @param {(index: number) => void} props.showData - Function to display the data of the specified row.
 * @param {(index: number) => void} props.deleteData - Function to delete the specified row.
 * @param {boolean} [props.draggable=true] - Determines if the row is draggable.
 * @param {number} props.index_real - The real index of the row, potentially different from the displayed index.
 *
 * @returns {JSX.Element} The rendered table row element with drag-and-drop functionality and action buttons.
 */
export const Row: React.FC<RowProps> = ({
  row,
  index,
  moveRow,
  showData,
  deleteData,
  draggable = true,
  index_real,
}) => {
  const dropRef = useRef<HTMLTableRowElement>(null);
  const dragRef = useRef<HTMLTableDataCellElement>(null);
  const noneRef = useRef<HTMLTableDataCellElement>(null);

  const [, drop] = useDrop<{ index: number; type: string }>({
    accept: DND_ITEM_TYPE,
    hover(item, monitor: DropTargetMonitor) {
      const { index: dragIndex } = item as { index: number; type: string };
      if (!dropRef.current) {
        return;
      }
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = dropRef.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      if (dragIndex === undefined || hoverIndex === undefined) {
        return;
      }

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_ITEM_TYPE,
    item: { type: DND_ITEM_TYPE, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;

  preview(drop(dropRef));
  drag(draggable ? dragRef : noneRef);

  return (
    <tr ref={dropRef} style={{ opacity }} data-testid={`table-row-${index}`}>
      <td ref={draggable ? dragRef : noneRef} style={{ cursor: "all-scroll" }} data-testid={`row-number-${index}`}>
        {index_real}
      </td>
      {row.cells.map((cell: any, cellIndex: number) => (
        <td {...cell.getCellProps()} data-testid={`cell-${index}-${cellIndex}`} key={cellIndex}>
          {cell.render("Cell")}
        </td>
      ))}
      <td style={{ minWidth: 90 }}>
        <span
          onClick={() => showData(index)}
          className="inline-flex align-center jcc bg-warning icon"
          style={{
            width: "2em",
            height: "2em",
            borderRadius: "7px",
            marginRight: 5,
          }}
          title="Gerenciar questão"
          data-testid={`view-button-${index}`}
        >
          <BsFillEyeFill size={17} color="white" />
        </span>
        <span
          onClick={() => deleteData(index)}
          className="inline-flex align-center jcc bg-danger icon"
          style={{ width: "2em", height: "2em", borderRadius: "7px" }}
          title="Deletar questão"
          data-testid={`delete-button-${index}`}
        >
          <MdDelete size={17} color="white" />
        </span>
      </td>
    </tr>
  );
};
